import { Box, Typography, useTheme, Avatar, styled, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { getThemeColor, GetRGBA } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { useGetDocumentReadStatus } from 'src/api/customer-admin'
import { CenteredSpinner } from 'src/components/Spinner'
import LinearProgress from '@mui/material/LinearProgress'
import { useTranslation } from 'react-i18next'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useState } from 'react'
import { UserVersionApprovalTable } from './UserVersionApprovalTable'

interface IGlobalDocumentApprovalStatusProps {
  customerId: EntityId
  documentId: EntityId
}

export const GlobalDocumentApprovalStatus: React.FC<IGlobalDocumentApprovalStatusProps> = ({
  customerId,
  documentId,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['document', 'common', 'global-document-status'])
  const { data, isLoading } = useGetDocumentReadStatus(customerId, documentId)
  const [expanded, setExpanded] = useState<string | false>(false)

  if (isLoading) return <CenteredSpinner />

  if (!data || data.users.length === 0)
    return <EmptyStatusCard content={t('noAssignedUsers', { ns: 'global-document-status' })} imageWidth="100px" />

  const approvalPercentage = data.totalUsers > 0 ? (data.approvedUsers / data.totalUsers) * 100 : 0

  const getProgressColor = () => {
    if (approvalPercentage < 33) return getThemeColor(theme, MUIThemeColors.errorMain)
    if (approvalPercentage < 66) return getThemeColor(theme, MUIThemeColors.warningMain)
    return getThemeColor(theme, MUIThemeColors.successMain)
  }

  const handleAccordionChange = (userId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? userId : false)
  }

  return (
    <ContainerBox>
      <ProgressSection theme={theme}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">{t('approval-progress', { ns: 'document' })}</Typography>
          <Typography variant="body2" fontWeight={600}>
            {approvalPercentage.toFixed(0)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={approvalPercentage}
          sx={{
            height: 8,
            borderRadius: 1,
            backgroundColor: `${getProgressColor()}20`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: getProgressColor(),
            },
          }}
        />
      </ProgressSection>

      <ScrollableUsersBox>
        {data.users.map(user => (
          <StyledAccordion
            key={user.userId}
            expanded={expanded === user.userId}
            onChange={handleAccordionChange(user.userId)}
            isApproved={user.isApproved}
            hasAccess={user.hasAccess}
          >
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <UserAvatar theme={theme}>{user.userName.charAt(0).toUpperCase()}</UserAvatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <UserNameTypography theme={theme} variant="body1">
                      {user.userName}
                    </UserNameTypography>
                    {!user.hasAccess && (
                      <AccessExpiredTag variant="caption">
                        {t('accessExpired', { ns: 'global-document-status' })}
                      </AccessExpiredTag>
                    )}
                  </Box>
                  <UserEmailTypography theme={theme} variant="body2">
                    {user.email}
                  </UserEmailTypography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                {user.isApproved && user.approvedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {new Date(user.approvedAt + 'Z').toISOString().split('T')[0]}
                  </Typography>
                )}
                <StatusBadge theme={theme} isApproved={user.isApproved} hasAccess={user.hasAccess} variant="body2">
                  {user.isApproved
                    ? t('approved', { ns: 'global-document-status' })
                    : user.hasAccess
                    ? t('pending', { ns: 'global-document-status' })
                    : t('notApproved', { ns: 'global-document-status' })}
                </StatusBadge>
              </Box>
            </StyledAccordionSummary>
            <AccordionDetails sx={{ background: 'none', pt: 1, pb: 3 }}>
              {expanded === user.userId && (
                <UserVersionApprovalTable customerId={customerId} documentId={documentId} userId={user.userId} />
              )}
            </AccordionDetails>
          </StyledAccordion>
        ))}
      </ScrollableUsersBox>
    </ContainerBox>
  )
}

const ContainerBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const ProgressSection = styled(Box)(({ theme }) => ({
  padding: '16px',
}))

const ScrollableUsersBox = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
})

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: prop => !['isApproved', 'hasAccess'].includes(prop as string),
})<{ isApproved: boolean; hasAccess: boolean }>(({ theme, isApproved, hasAccess }) => ({
  padding: '8px 16px',
  marginBottom: '8px',
  boxShadow: 'none',
  border: `1px solid ${
    isApproved ? getThemeColor(theme, MUIThemeColors.successMain) : getThemeColor(theme, MUIThemeColors.warningMain)
  }`,
  borderRadius: '5px',
  backgroundColor: isApproved
    ? `${getThemeColor(theme, MUIThemeColors.successMain)}15`
    : `${getThemeColor(theme, MUIThemeColors.warningMain)}15`,
  '&:before': { display: 'none' },
  ...(!hasAccess && {
    border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}`,
    backgroundColor: GetRGBA(getThemeColor(theme, MUIThemeColors.secondaryMain), 0.08),
  }),
}))

const StyledAccordionSummary = styled(AccordionSummary)({
  padding: 0,
  minHeight: 0,
  '& .MuiAccordionSummary-content': {
    margin: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
})

const UserAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.primaryMain)}1A`,
  width: 40,
  height: 40,
}))

const UserNameTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
}))

const AccessExpiredTag = styled(Typography)(({ theme }) => ({
  padding: '0 6px',
  fontWeight: 500,
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}`,
  borderRadius: '5px',
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))

const UserEmailTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))

const StatusBadge = styled(Typography, {
  shouldForwardProp: prop => !['isApproved', 'hasAccess'].includes(prop as string),
})<{ theme: any; isApproved: boolean; hasAccess: boolean }>(({ theme, isApproved, hasAccess }) => ({
  padding: '8px',
  minWidth: '100px',
  textAlign: 'center',
  fontWeight: 500,
  border: `1px solid ${
    isApproved ? getThemeColor(theme, MUIThemeColors.successMain) : getThemeColor(theme, MUIThemeColors.warningMain)
  }`,
  borderRadius: '5px',
  color: isApproved
    ? getThemeColor(theme, MUIThemeColors.successMain)
    : getThemeColor(theme, MUIThemeColors.warningMain),
  backgroundColor: isApproved
    ? `${getThemeColor(theme, MUIThemeColors.successLight)}20`
    : `${getThemeColor(theme, MUIThemeColors.warningLight)}20`,
  ...(!hasAccess && {
    border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}`,
    color: getThemeColor(theme, MUIThemeColors.secondaryMain),
    backgroundColor: GetRGBA(getThemeColor(theme, MUIThemeColors.secondaryLight), 0.12),
  }),
}))
